import React from "react";
import {
  FaEdit,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaTrash,
} from "react-icons/fa";

import {
  buildGoogleMapLink,
  getInfrastructurePhotos,
  hasGps,
  resolveMediaUrl,
  truncateText,
} from "../utils/infrastructuresManagerUtils";

export default function InfrastructureTable({
  infrastructures,
  loading,
  totalRows,
  visibleCount,
  demoMode,
  onLoadMore,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Tableau des infrastructures
        </h2>

        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {totalRows.toLocaleString("fr-FR")} infrastructure
          {totalRows > 1 ? "s" : ""} trouvée{totalRows > 1 ? "s" : ""}.
        </p>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1180px] w-full text-sm">
            <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="sticky left-0 z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Photos
                </th>

                <th className="sticky left-[118px] z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Infrastructure
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Adresse
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Description
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  GPS
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Ordre
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={7} className="px-4 py-3">
                      <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : infrastructures.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucune infrastructure ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                infrastructures.map((infrastructure) => {
                  const photos = getInfrastructurePhotos(infrastructure);
                  const firstPhoto = resolveMediaUrl(photos[0]);
                  const secondPhoto = resolveMediaUrl(photos[1]);
                  const gpsOk = hasGps(infrastructure);

                  return (
                    <tr
                      key={infrastructure.id}
                      className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <div className="flex gap-2">
                          <img
                            src={firstPhoto}
                            alt={infrastructure.nom_infrastructure}
                            className="h-14 w-14 object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                            onError={(event) => {
                              event.currentTarget.src =
                                "/images/infrastructure_placeholder.jpg";
                            }}
                          />

                          <img
                            src={secondPhoto}
                            alt={infrastructure.nom_infrastructure}
                            className="h-14 w-14 object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                            onError={(event) => {
                              event.currentTarget.src =
                                "/images/infrastructure_placeholder.jpg";
                            }}
                          />
                        </div>
                      </td>

                      <td className="sticky left-[118px] z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <div className="font-bold text-slate-900 dark:text-white">
                          {infrastructure.nom_infrastructure || "—"}
                        </div>

                        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          ID {infrastructure.id}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <div className="max-w-[260px] leading-6">
                          {infrastructure.adresse_infrastructure || "—"}
                        </div>

                        {(infrastructure.adresse_infrastructure ||
                          infrastructure.gps_infrastructure) && (
                          <a
                            href={buildGoogleMapLink(infrastructure)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary"
                          >
                            Ouvrir la carte
                            <FaExternalLinkAlt size={10} />
                          </a>
                        )}
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <div className="max-w-[320px] leading-6">
                          {truncateText(
                            infrastructure.description_infrastructure,
                            150
                          ) || "—"}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1",
                            gpsOk
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                              : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
                          ].join(" ")}
                        >
                          <FaMapMarkerAlt />
                          {gpsOk ? infrastructure.gps_infrastructure : "Manquant"}
                        </span>
                      </td>

                      <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">
                        {infrastructure.ordre_infrastructure ?? 1}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(infrastructure)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                            title="Modifier"
                          >
                            <FaEdit />
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(infrastructure)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && visibleCount < totalRows && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={onLoadMore}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10"
            >
              Afficher plus ({visibleCount} / {totalRows})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}